import { Directive, ElementRef, HostListener, Input, OnDestroy, inject } from '@angular/core';
import { ConnectedPosition, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { CampaignCardOverlayComponent } from 'app/campaign/campaigns/campaign-card-overlay/campaign-card-overlay.component';

const OPEN_DELAY_MS = 250;
const CLOSE_DELAY_MS = 100;
const POSITIONS: ConnectedPosition[] = [
  { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 },
  { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
];

@Directive({
  selector: '[appCampaignPreviewHover]',
  standalone: false,
})
export class CampaignPreviewHoverDirective implements OnDestroy {
  @Input('appCampaignPreviewHover') imageUrl: string | undefined;
  @Input() previewName = '';

  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly overlay = inject(Overlay);

  private overlayRef: OverlayRef | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;

  @HostListener('mouseenter')
  protected onMouseEnter(): void {
    if (!this.imageUrl) {
      return;
    }
    this.schedule(() => this.open(), OPEN_DELAY_MS);
  }

  @HostListener('mouseleave')
  protected onMouseLeave(): void {
    this.schedule(() => this.close(), CLOSE_DELAY_MS);
  }

  // A click on the host navigates or opens a flyout, which the card would cover.
  @HostListener('click')
  protected onClick(): void {
    this.clearTimer();
    this.close();
  }

  ngOnDestroy(): void {
    this.clearTimer();
    this.close();
  }

  private open(): void {
    this.close();
    this.overlayRef = this.overlay.create({
      positionStrategy: this.overlay.position().flexibleConnectedTo(this.host).withPositions(POSITIONS).withPush(true),
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
    });
    const card = this.overlayRef.attach(new ComponentPortal(CampaignCardOverlayComponent));
    card.instance.imageUrl = this.imageUrl;
    card.instance.name = this.previewName;
    card.changeDetectorRef.detectChanges();
  }

  private close(): void {
    this.overlayRef?.dispose();
    this.overlayRef = null;
  }

  private schedule(action: () => void, delayMs: number): void {
    this.clearTimer();
    this.timer = setTimeout(() => {
      this.timer = null;
      action();
    }, delayMs);
  }

  private clearTimer(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }
}
